import { useTranslation } from 'react-i18next'
import { Formik, Form } from 'formik'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import { InputLabel, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import { css } from '@emotion/css'
import { Link, useLocation } from 'react-router-dom'

import {
  AppVersion,
  AuthContentWrapper,
  AuthFormWrapper,
  AuthHero,
  HelperWrapper,
  TextInputWithHelper,
} from 'src/components'
import { useForgotPassword } from 'src/hooks'
import { ETranslationNs, IForgotPasswordDto } from 'src/types'
import { TrackgoodLogo } from 'src/assets/icons'

import { emailResetSchema } from 'src/common/validations'
import { FONTS, ROUTES, SPACES } from 'src/constants'
import useStyles from '../styles'
import { InputStyles, SubmitBtn } from './index.styled'

const ForgotPassword = () => {
  const [t] = useTranslation()
  const { mutateAsync: forgotPassword } = useForgotPassword()
  const theme = useTheme()
  const location = useLocation()

  const classes = useStyles()
  const isLaptop = useMediaQuery(theme.breakpoints.down('laptop'))
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))
  const isMobile = useMediaQuery(theme.breakpoints.down('mobile_s'))

  const handleSubmit = async (values: IForgotPasswordDto) => {
    await forgotPassword(values)
  }

  return (
    <AuthContentWrapper>
      <AuthHero />
      <Grid px={isLaptop ? SPACES.xxxl : 0} item xs className={classes.contentWrapper}>
        <Grid
          display={isLaptop ? 'none' : 'flex'}
          justifyContent="flex-end"
          className={classes.signUpContainer}
        >
          <Typography className={classes.textSignUp} alignItems={'center'}>
            <HelperWrapper t="forgot-password-opposite-text" />{' '}
            <Link style={{ textDecoration: 'none' }} to={ROUTES.LOGIN}>
              <Button className={classes.buttonSignUp}>
                <HelperWrapper t="login-link-text" />
              </Button>
            </Link>
          </Typography>
        </Grid>
        <AuthFormWrapper>
          <Formik
            initialValues={{
              email: location?.state?.email ?? '',
              journeyRedirect: (location?.state?.journeyRedirect as string) ?? '',
            }}
            onSubmit={handleSubmit}
            validationSchema={emailResetSchema}
          >
            {({ values, handleChange, errors, isSubmitting }) => (
              <Form>
                <Grid container direction="column" justifyContent="center" alignItems="center">
                  <Grid>
                    <TrackgoodLogo className={classes.labelMobile} />
                    <Grid item xs={12} className={classes.containerTitle}>
                      <Typography fontWeight={'700'} mb={SPACES.xxxs} fontSize={FONTS.SIZES.xxxxl}>
                        <HelperWrapper t="forgot-password-title" />
                      </Typography>
                      <Typography
                        display={isLaptop ? 'block' : 'none'}
                        className={classes.textSignUp}
                        alignItems={'center'}
                      >
                        <HelperWrapper t="forgot-password-subtitle" />
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      className={classes.containerField}
                      sx={{ marginBottom: '20px' }}
                    >
                      <TextInputWithHelper
                        autoComplete="on"
                        fullWidth
                        inputName="email"
                        handleChange={handleChange}
                        value={values.email}
                        error={errors.email ? t(errors.email + '.v') : ''}
                        inputPlaceholder={t('account-form-email-input-placeholder.v', {
                          ns: ETranslationNs.Form,
                        })}
                        inputUpperLabel={
                          <HelperWrapper
                            t={'email-input-label'}
                            ns={ETranslationNs.Common}
                            isInputLable
                            isMandatory
                          />
                        }
                      />
                    </Grid>
                    <Grid item>
                      <Button
                        disabled={isSubmitting}
                        type="submit"
                        variant="contained"
                        color="secondary"
                        sx={{ color: '#fff' }}
                        className={css(SubmitBtn, isMobile && `width: 90%;`)}
                      >
                        <HelperWrapper t="forgot-password-submit-button" />
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </AuthFormWrapper>
        <footer className={classes.footerWrapper}>
          <AppVersion />
        </footer>
      </Grid>
    </AuthContentWrapper>
  )
}

export default ForgotPassword
